<template>
  <v-card v-if="project">
    <v-card-title>
      <v-flex xs6>
        <v-avatar size=48>
          <img :src="logoThumbUrl" alt="Avatar" v-if="logoThumbUrl">
          <v-icon color="primary" large v-else>ballot</v-icon>
        </v-avatar>
        <span class="subheading pl-2">
          {{project.attributes.name}}
        </span>
      </v-flex>
    </v-card-title>

    <v-card-text>
      <v-form
        ref="form"
        lazy-validation
        >
        <v-layout row wrap>
          <v-flex xs8 class="pr-2">
            <v-text-field
              name="name"
              label="Name"
              prepend-icon=" "
              v-model="localProject.name"
              :maxlength="maxCharacters"
              :counter="maxCharacters"
              :rules="[rules.required]"
            ></v-text-field>
            <v-textarea
              label="Description"
              name="description"
              v-model="localProject.description"
              :maxlength="maxCharacters"
              :counter="maxCharacters"
              prepend-icon="short_text"
              class="mb-3"
            ></v-textarea>
          </v-flex>

          <v-flex xs4 :style="'background: url(' + logoUrl + ') no-repeat center;'" >
            <vue-dropzone ref='logoDropzone' id='dropzone' :options='dropzoneOptions' @vdropzone-queue-complete="dzQueueComplete" style="opacity: 0.9;"></vue-dropzone>
          </v-flex>
        </v-layout>

      </v-form>

      <v-expansion-panel
        v-model="statusesPanel"
        expand
        class="mt-3"
        >
        <v-expansion-panel-content ripple class="v-expansion-panel-setting">
          <template v-slot:header>
            <v-flex class="subheading white--text">Statuses</v-flex>
          </template>

          <v-layout row wrap class="font-weight-bold ml-2 mr-2">
            <v-flex xs4 class="pt-2">Name</v-flex>
            <v-flex xs6 class="pt-2 pl-3">Color</v-flex>
            <v-flex xs2 class="pt-2">Open</v-flex>
          </v-layout>
          <hr>

          <draggable v-model="statuses" @end="statusMoved" handle=".handle" class="mt-1">
            <v-layout row wrap v-for="(status, idx) in statuses" :key="status.id" class="status mt-3">
              <v-flex xs1 class="handle"><v-icon>drag_indicator</v-icon></v-flex>

              <v-flex xs3 v-if="status.attributes.system">
                {{status.attributes.name}}
              </v-flex>
              <v-flex xs3 v-else>
                <v-text-field hide-details class="ma-0 pa-0" v-model="status.attributes.name" @change="updateStatus(status)"></v-text-field>
              </v-flex>

              <v-flex xs6 class="pl-3 pr-3 pt-2"><Slider v-model="status.attributes.color" @input="updateStatus(status)"/></v-flex>

              <v-flex xs1 v-if="status.attributes.system">
                <v-switch class="mt-0 pt-0"  disabled v-model="status.attributes.open"></v-switch>
              </v-flex>
              <v-flex xs1 v-else>
                <v-switch class="mt-0 pt-0" v-model="status.attributes.open" @change="updateStatus(status)"></v-switch>
              </v-flex>

              <v-flex xs1 class="text-xs-right pr-3"><v-icon @click="deleteStatus(status)" v-if="!status.attributes.system">delete</v-icon></v-flex>
            </v-layout>
            <br>
          </draggable>

          <v-layout row wrap>
            <v-flex xs12 class="text-xs-right">
              <v-btn small flat outline color="success" @click="createStatus()">
                <v-icon>add</v-icon>
                Add status
              </v-btn>
            </v-flex>
          </v-layout>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel
        v-model="membersPanel"
        expand
        class="mt-3"
        >
        <v-expansion-panel-content ripple class="v-expansion-panel-setting">
          <template v-slot:header>
            <v-flex class="subheading white--text">Members</v-flex>
          </template>

          <v-layout row wrap class="font-weight-bold ml-2 mr-2">
            <v-flex xs4 class="pt-2">Email</v-flex>
            <v-flex xs4 class="pt-2">Name</v-flex>
            <v-flex xs4 class="pt-2">Access</v-flex>
          </v-layout>
          <hr>

          <v-layout row wrap v-for="(member, idx) in members" :key="member.id" class="member mt-3">
            <v-flex xs4 class="pl-2 pr-2">
              {{member.attributes.email}}
            </v-flex>
            <v-flex xs4 class="pl-2 pr-2">
              {{member.attributes.email !== member.attributes.name ? member.attributes.name : ''}}
            </v-flex>

            <v-flex xs3>
              <v-combobox v-if="permit.canEditMember(member)"
                v-model="member.attributes.role"
                :items="allowedRoles"
                class="mt-0 pt-0"
              ></v-combobox>
              <span v-else>{{member.attributes.role}}</span>
            </v-flex>

            <v-flex xs1 class="text-xs-right pr-3"><v-icon @click="deleteMember(idx, member)" v-if="canDeleteMember(member)">delete</v-icon></v-flex>
          </v-layout>

          <v-layout row wrap v-for="(member, idx) in newMembers" :key="member.id" class="member mt-3">
            <v-flex xs8 class="pl-2 pr-2">
              <v-text-field
                class="ma-0 pa-0"
                v-model="member.attributes.email"
                placeholder="email@example.com"
                :rules="[rules.required]"
              ></v-text-field>
            </v-flex>

            <v-flex xs3>
              <v-combobox
                v-model="member.attributes.role"
                :items="allowedRoles"
                class="mt-0 pt-0"
              ></v-combobox>
            </v-flex>

            <v-flex xs1 class="text-xs-right pr-3"><v-icon @click="removeMember(idx)">delete</v-icon></v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex xs12 class="text-xs-right">
              <v-btn small color="info" @click="processInvitations()" :disabled="(newMembers.length < 1) || busy" :loading="busy">
                <span v-if="newMembers.length < 2">
                  Invite member
                </span>
                <span v-else>
                  Invite {{newMembers.length}} members
                </span>
                <v-icon class="pl-3">send</v-icon>
              </v-btn>

              <v-btn small flat outline color="success" @click="addMember()">
                <v-icon>add</v-icon>
                Add member
              </v-btn>

            </v-flex>
          </v-layout>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel
        v-model="keysPanel"
        expand
        class="mt-3"
        >
        <v-expansion-panel-content ripple class="v-expansion-panel-setting">
          <template v-slot:header>
            <v-flex class="subheading white--text">Integrations</v-flex>
          </template>

          <v-layout row wrap class="font-weight-bold ml-2 mr-2">
            <v-flex xs4 class="pt-2">Name</v-flex>
            <v-flex xs8 class="pt-2">Token</v-flex>
          </v-layout>
          <hr>

          <v-layout row wrap v-for="(key, idx) in keys" :key="key.id" class="key mt-3">
            <v-flex xs4 class="pl-2 pr-2">
              <v-text-field hide-details class="ma-0 pa-0" v-model="key.attributes.name" @change="updateKey(key)"></v-text-field>
            </v-flex>

            <v-flex xs7>
              {{key.attributes.access_key}}
            </v-flex>

            <v-flex xs1 class="text-xs-right pr-3"><v-icon @click="deleteKey(idx, key)" v-if="!key.attributes.system">delete</v-icon></v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex xs12 class="text-xs-right">
              <v-btn small flat outline color="success" @click="createKey()">
                <v-icon>add</v-icon>
                Add key
              </v-btn>
            </v-flex>
          </v-layout>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn flat color="primary" @click="done()">Done</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
  import { EventBus } from '@/utilities/event-bus'
  import authentication from '@/utilities/authentication'
  import permissions from '@/utilities/permissions'

  import vueDropzone from 'vue2-dropzone'
  import 'vue2-dropzone/dist/vue2Dropzone.min.css'

  import draggable from 'vuedraggable'
  import { Slider } from 'vue-color'

  import projectService from '@/services/project'
  import statusService from '@/services/status'
  import keyService from '@/services/key'
  import memberService from '@/services/member'
  import invitationService from '@/services/invitation'

  export default {
    name: 'project-edit',

    props: ['project'],

    data () {
      return {
        auth: authentication,
        permit: permissions,
        maxCharacters: 100,
        localProject: {},
        members: [],
        newMembers: [],
        statuses: [],
        statusesPanel: [],
        membersPanel: [],
        keys: [],
        keysPanel: [],
        dropzoneOptions: {
          url: process.env.API_URL + this.project.links.update_uri + '.json',
          method: 'put',
          paramName: 'project[logo]',
          thumbnailWidth: 150,
          maxFilesize: 5.0,
          maxFiles: 1,
          acceptedFiles: 'image/*',
          headers: {
            'X-User-Email': authentication.username(),
            'X-User-Token': authentication.token()
          },
          dictDefaultMessage: 'Replace your project logo',
          // we dnot want DZ to auto-upload the files, we need to make sure it saves first
          autoProcessQueue: false
        },
        busy: false,
        rules: {
          required: value => !!value || 'Required'
        }
      }
    },

    components: {
      vueDropzone,
      draggable,
      Slider
    },

    computed: {
      logoThumbUrl () {
        if (this.project && this.project.links && this.project.links.logo_thumb_uri) {
          return (process.env.API_URL + this.project.links.logo_thumb_uri)
        }
        return null
      },

      logoUrl () {
        if (this.project && this.project.links && this.project.links.logo_uri) {
          return (process.env.API_URL + this.project.links.logo_uri)
        }
        return null
      },

      storeUrl () {
        return this.project.links.purchase_uri
      },

      allowedRoles () {
        let roleMap = {
          'owner': ['restricted', 'regular', 'administrator', 'owner'],
          'administrator': ['restricted', 'regular', 'administrator'],
          'regular': ['restricted', 'regular'],
          'restricted': ['restricted']
        }

        let current = this.members.find(m => this.auth.isCurrentUser(m.relationships.user.data.id))
        return roleMap[current.attributes.role]
      }
    },

    mounted () {
      this.localProject = Object.assign({}, this.project.attributes)
      this.loadStatuses()
      this.loadKeys()
      this.loadMembers()
    },

    methods: {
      loadStatuses () {
        statusService.projectIndex(
          (data) => {
            this.statuses = data.sort((a, b) => { return parseInt(a.position) - parseInt(b.position) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      loadKeys () {
        keyService.projectIndex(
          (data) => {
            this.keys = data.sort((a, b) => {
              a.attributes.name = a.attributes.name || ''
              b.attributes.name = b.attributes.name || ''
              return a.attributes.name.toLowerCase().localeCompare(b.attributes.name.toLowerCase())
            })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      loadMembers () {
        memberService.projectIndex(
          (data) => {
            this.members = data.filter(m => !m.attributes.discarded_at).sort((a, b) => { return a.attributes.name.toLowerCase().localeCompare(b.attributes.name.toLowerCase()) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      done () {
        // NOTE: roll back any changes we made (project is passed in by reference)
        this.localProject = Object.assign({}, this.project.attributes)
        this.loadStatuses()
        this.loadKeys()
        this.loadMembers()
        this.$refs.logoDropzone.removeAllFiles()
        EventBus.$emit('project-updated')
      },

      // NOTE: we dont want to save the updated logos until the users clicks save, so we process
      // those on a second pass once the form saved.
      updateProject () {
        if (this.$refs.form.validate()) {
          this.busy = true
          projectService.update(
            async (data) => {
              this.localProject = Object.assign({}, data.attributes)

              if (this.$refs.logoDropzone.getQueuedFiles().length > 0) {
                this.$refs.logoDropzone.processQueue()
              } else {
                // no files queued, so just pretend we are done
                this.dzQueueComplete()
              }
            },
            (err) => {
              this.busy = false
              console.log(err)
            },
            this.localProject
          )
        }
      },

      dzQueueComplete (file, xhr, formData) {
        this.$refs.logoDropzone.removeAllFiles()

        // NOTE: there is a catch here: when we save the project we get the links to the OLD images. We need to reload the project once the new image is saved, so we
        // can send that back and the caller does not have to deal with it.
        projectService.show(
          (data) => {
            this.busy = false
            EventBus.$emit('project-updated', data)
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      createStatus () {
        statusService.projectCreate(
          (data) => {
            this.statuses.push(Object.assign({}, data))
          },
          (err) => {
            console.log(err)
          },
          this.localProject.id,
          {
            name: 'name',
            color: '#FFFFFF',
            system: false,
            open: true,
            position: this.statuses.length
          }
        )
      },

      updateStatus (status) {
        let idx = this.statuses.findIndex(s => parseInt(s.id) === parseInt(status.attributes.id))
        status.attributes.position = idx

        // clean up color values (the picker adds lots we dont need, we just want the hex)
        if (status.attributes.color instanceof Object) {
          status.attributes.color = status.attributes.color.hex
        } // else we assume it's already a hex string

        statusService.update(
          (data) => {
            // nothing realy to do here
          },
          (err) => {
            console.log(err)
          },
          status.attributes
        )
      },

      deleteStatus (status) {
        statusService.delete(
          (data) => {
            let idx = this.statuses.findIndex(s => parseInt(s.id) === parseInt(status.attributes.id))
            if (idx >= 0) {
              this.statuses.splice(idx, 1)
            }
          },
          (err) => {
            console.log(err)
          },
          status.attributes.id
        )
      },

      statusMoved (evt) {
        // update all the status positions
        for (let i = 0; i < this.statuses.length; i++) {
          this.statuses[i].attributes.position = i
          this.updateStatus(this.statuses[i])
        }
      },

      createKey () {
        keyService.projectGenerate(
          (data) => {
            console.log('key generated: ', data)
            keyService.projectCreate(
              (data) => {
                this.keys.push(Object.assign({}, data))
              },
              (err) => {
                console.log(err)
              },
              this.localProject.id,
              {access_key: data.key}
            )
          },
          (err) => {
            console.log(err)
          },
          this.localProject.id
        )
      },

      updateKey (key) {
        keyService.projectUpdate(
          (data) => {
            // nothing realy to do here
          },
          (err) => {
            console.log(err)
          },
          this.localProject.id,
          key.attributes
        )
      },

      deleteKey (idx, key) {
        keyService.projectDelete(
          (data) => {
            this.keys.splice(idx, 1)
          },
          (err) => {
            console.log(err)
          },
          this.localProject.id,
          key.attributes.id
        )
      },

      canDeleteMember (member) {
        // if its a brand new member we can remove it
        if (!member.id) {
          return true
        }

        // cant delete ourselves
        if (this.auth.isCurrentUser(member.relationships.user.data.id)) {
          return false
        }

        // cant delete owners ever
        return this.allowedRoles.includes(member.attributes.role) && !(member.attributes.role === 'owner')
      },

      canUpdateMember (member) {
        // if its a brand new member we can update it
        if (!member.id) {
          return true
        }

        // cant update an owner unless it's you and there is at least one more
        if (this.auth.isCurrentUser(member.relationships.user.data.id)) {
          let owners = this.members.filter(m => m.id && (m.attributes.role === 'owner') && !this.auth.isCurrentUser(m.relationships.user.data.id))
          return (owners.length > 0)
        } else {
          return this.allowedRoles.includes(member.attributes.role) && !(member.attributes.role === 'owner')
        }
      },

      processInvitations () {
        // send out all the invites
        for (let i = 0; i < this.newMembers.length; i++) {
          this.inviteMember(i, this.newMembers[i])
        }
      },

      inviteMember (idx, member) {
        console.log('inviting member at index ' + idx + ' from newMembers: ', member)
        return invitationService.projectCreate(
          (data) => {
            // take the invite off the list, it's on the main list now
            this.removeMember(idx)
            console.log('invited member: ', data)
            this.members.push(Object.assign({}, data))
          },
          (err) => {
            console.log(err)
          },
          this.localProject.id,
          member.attributes
        )
      },

      addMember () {
        // push a new unsaved membership on to the list
        this.newMembers.push({attributes: {email: '', role: this.allowedRoles[0]}})
      },

      removeMember (idx) {
        console.log('removing member at index ' + idx + ' from newMembers')
        // remove a new unsaved membership on to the list
        this.newMembers.splice(idx, 1)
      },

      updateMember (member) {
        memberService.projectUpdate(
          (data) => {
            // nothing realy to do here
          },
          (err) => {
            console.log(err)
          },
          this.localProject.id,
          member.attributes
        )
      },

      deleteMember (idx, member) {
        memberService.projectDelete(
          (data) => {
            this.members.splice(idx, 1)
          },
          (err) => {
            console.log(err)
          },
          this.localProject.id,
          member.attributes.id
        )
      }
    }
  }
</script>

<style lang="scss" scoped>
  .v-expansion-panel-setting {
    background-color: #0155a8 !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/project/_edit.vue